import { trimApartment } from './vibeResultSnapshot';
/**
 * vibeStore — Domain: Conversational apartment search
 * 
 * Owns ALL vibe search state: conversations, results, sessions.
 * Both VibeSearchPage and MapVibeChat subscribe here.
 * 
 * Responsibilities:
 * - Session management (create, switch, persist)
 * - Message history per session  
 * - Search results per session
 * - API communication with vibe search backend
 * - Summary fetching
 * 
 * Does NOT own: map visual state, which pin is highlighted, pan targets
 * (viewport state lives in the map components)
 */
import { create } from 'zustand';
import { resolveResumableSessionId } from '@/stores/vibeSessionResume';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Apartment } from '../types/apartment';
import { useUserProfileStore } from './userProfileStore';
import { useApartmentStore } from './apartmentStore';
import { useSearchStore, restoreConversationInNewTab } from './search';
import { resetSearchState } from './searchSession';
import { toVibeContext } from './filterTransform';
import { fetchWithTimeout } from '@/utils/fetchWithTimeout';
import { safeLocal } from '@/utils/safeStorage';
import { useAuthStore } from './authStore';
import type { ConversationMode, ConversationSender, LiveConversationMessage } from '../types/conversation';

// Same-origin in the browser. The build-time env var is intentionally NOT
// used here because stale production builds have shipped with
// REACT_APP_BACKEND_URL pointing at a preview host, breaking vibe search
// after a domain change. authStore and apartmentStore follow the same rule.
const API = typeof window !== 'undefined' ? window.location.origin : '';

// Backend-suggested clickable actions ("pills") shown below an assistant
// message. Phase 2 PR #1 introduces two kinds:
//   send_message    — frontend dispatches `message` as if user typed it
//   update_criteria — frontend patches apartmentStore.filters with `patch`
//                     (camelCase FiltersState keys), then dispatches
//                     `message` so the conversation log reflects the change
export type VibeAction =
  | { id: string; label: string; kind: 'send_message'; message: string }
  | { id: string; label: string; kind: 'update_criteria'; patch: Record<string, unknown>; message: string };

export interface VibeChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sender?: Exclude<ConversationSender, 'renter'>;
  messageId?: string;
  apartments?: Apartment[];
  criteria?: Record<string, unknown>;
  neighborhoodGroups?: { neighborhood: string; apartment_names: string[]; count: number; top_score: number }[];
  isLoading?: boolean;
  // Server-templated progress lines for an in-flight agentic turn, rendered
  // while isLoading. Generated from the tool calls that actually ran (see
  // backend services/vibe_agent/narration.py) — never model prose — so a line
  // on screen is proof of work done, not a claim about it.
  narration?: string[];
  // The agent's closing prose as it streams (message_delta frames), shown
  // under the early cards while isLoading. Replaced by `content` at `done`.
  streamingText?: string;
  // Render-only: a streaming bubble (hides actions) / a final message whose
  // text the renter already watched stream in (skips the typewriter).
  isStreaming?: boolean;
  wasStreamed?: boolean;
  // Phase 4 — rate-limit response marker
  isRateLimited?: boolean;
  isAnonymous?: boolean;
  // Board 5ON-0 — network/send failure marker; the UI renders these as the
  // ERROR StateCard with a Retry that re-sends the last user message.
  isError?: boolean;
  // §4.2 — zero-results diagnosis (backend-computed)
  diagnosis?: { filter: string; label: string; unlocks: number } | null;
  // Phase 2 PR #1 — backend-suggested clickable refinement actions.
  actions?: VibeAction[];
  // One-thread catch-up turn (client template, never sent from the backend).
  isCatchUp?: boolean;
  // The backend asked the renter something rather than answering. Distinct
  // from "a search that found nothing": a question turn is waiting on a reply,
  // so the UI must not dress it up as an empty result set (no zero-state card,
  // no "new search" nudge).
  isQuestion?: boolean;
  // A question turn that withheld off-target matches rather than passing them
  // off as results. Count only — the matches themselves are deliberately not
  // rendered; showing them would recreate the behavior this replaced.
  looseMatchCount?: number;
  // A market sample shown UNDER a question turn — real listings, but not the
  // answer to what the renter asked. The UI labels them so the sample can't
  // read as off-target filler dressed up as results.
  isProvisional?: boolean;
  // Inline "want Alexander to check these?" card. Present on at most one turn
  // per session; the backend decides when the renter has shown enough intent.
  captureOffer?: VibeCaptureOffer | null;
  // Flipped once the renter submits, so the card becomes a confirmation
  // instead of vanishing (a disappearing form reads as a failed submit).
  captureDone?: string | null;
}

export interface VibeCaptureOffer {
  reason: string;
  headline: string;
  fields: string[];
  apartments: string[];
  cta: string;
}

interface VibeSession {
  id: string;
  messages: VibeChatMessage[];
  hasResults: boolean;
  city: string;
  persistedResults: Apartment[];
  persistedCriteria: Record<string, unknown> | null;
  mapAction: Record<string, unknown> | null;
  conversationToken: string;
  humanMode?: ConversationMode;
  humanRequested?: boolean;
  /** Last-activity stamp — drives the 3-sessions / 7-day persistence pruning. */
  updatedAt?: number;
  /** What the renter's shared state looked like when they last had the
   *  thread open — the catch-up turn diffs against this. */
  contextSnapshot?: ThreadContextSnapshot;
  searchFilters?: ReturnType<typeof useSearchStore.getState>['filters'];
}

interface ThreadContextSnapshot {
  bedrooms: string[];
  maxBudget: string | number | null;
  neighborhoods: string[];
  savedNames: string[];
}

function captureThreadContext(): ThreadContextSnapshot {
  const f = useSearchStore.getState().filters || ({} as Record<string, unknown>);
  let savedNames: string[] = [];
  try {
    savedNames = (JSON.parse(safeLocal.get('tacostreet_local_saves') || '[]') as Array<{ name?: string }>)
      .map(a => a?.name || '').filter(Boolean);
  } catch { /* private mode etc. */ }
  return {
    bedrooms: ((f as { bedrooms?: string[] }).bedrooms || []).slice(),
    maxBudget: (f as { maxBudget?: string | number | null }).maxBudget ?? null,
    neighborhoods: ((f as { neighborhoods?: string[] }).neighborhoods || []).slice(),
    savedNames,
  };
}

/** Human-readable diff between snapshots; empty array = nothing to catch up on. */
function diffThreadContext(prev: ThreadContextSnapshot, curr: ThreadContextSnapshot): string[] {
  const parts: string[] = [];
  const newSaves = curr.savedNames.filter(n => !prev.savedNames.includes(n));
  if (newSaves.length === 1) parts.push(`saved ${newSaves[0]}`);
  else if (newSaves.length === 2) parts.push(`saved ${newSaves[0]} and ${newSaves[1]}`);
  else if (newSaves.length > 2) parts.push(`saved ${newSaves[0]} and ${newSaves.length - 1} more`);
  const hoodsChanged = JSON.stringify(prev.neighborhoods) !== JSON.stringify(curr.neighborhoods);
  if (hoodsChanged && curr.neighborhoods.length > 0) {
    parts.push(`focused on ${curr.neighborhoods.slice(0, 2).join(' and ')}${curr.neighborhoods.length > 2 ? ' (and more)' : ''}`);
  }
  if (String(prev.maxBudget ?? '') !== String(curr.maxBudget ?? '') && curr.maxBudget) {
    parts.push(`moved your budget to under $${Number(curr.maxBudget).toLocaleString()}`);
  }
  if (JSON.stringify(prev.bedrooms) !== JSON.stringify(curr.bedrooms) && curr.bedrooms.length > 0) {
    parts.push(`switched to ${curr.bedrooms.join('/').replace(/\s*bedrooms?/gi, 'BR')}`);
  }
  return parts;
}

// ── Persistence (plan R2-1, 2026-07-07) ────────────────────────────────────
// Conversations used to be memory-only: a reload wiped the renter's entire
// AI conversation mid-apartment-hunt. Sessions now persist to localStorage
// with three guardrails against the ~5MB quota:
//   1. Apartment objects are trimmed to the card-essential fields; anything
//      deeper rehydrates on demand (PropertyDetailModal already refetches).
//   2. Only the 3 most recently active sessions are written.
//   3. Sessions idle for 7+ days are pruned on rehydrate.

const PERSIST_MAX_SESSIONS = 3;
const PERSIST_TTL_MS = 7 * 24 * 60 * 60 * 1000;



function trimSessionForStorage(sess: VibeSession): VibeSession {
  return {
    ...sess,
    messages: sess.messages
      .filter(m => !m.isLoading)
      .map(m => (m.apartments?.length ? { ...m, apartments: m.apartments.map(trimApartment) } : m)),
    persistedResults: sess.persistedResults.slice(0, 30).map(trimApartment),
  };
}

function pruneSessions(
  sessions: Record<string, VibeSession>,
  activeSessionId: string | null,
): { sessions: Record<string, VibeSession>; activeSessionId: string | null } {
  const now = Date.now();
  const alive = Object.values(sessions)
    .filter(s => now - (s.updatedAt ?? 0) < PERSIST_TTL_MS)
    .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
    .slice(0, PERSIST_MAX_SESSIONS);
  const next: Record<string, VibeSession> = {};
  for (const s of alive) next[s.id] = s;
  return {
    sessions: next,
    activeSessionId: activeSessionId && next[activeSessionId] ? activeSessionId : null,
  };
}

interface VibeStoreState {
  // Sessions
  sessions: Record<string, VibeSession>;
  activeSessionId: string | null;

  // Derived convenience getters
  activeSession: () => VibeSession | null;
  activeMessages: () => VibeChatMessage[];
  activeHasResults: () => boolean;
  latestResults: () => Apartment[];
  persistedResults: () => Apartment[];
  persistedCriteria: () => Record<string, unknown> | null;
  latestMapAction: () => Record<string, unknown> | null;

  // Loading
  isSearching: boolean;

  // Actions
  createSession: (city: string, source?: string, opts?: { resetState?: boolean }) => string;
  setActiveSession: (id: string) => void;
  sendMessage: (
    text: string,
    mapContext?: Record<string, unknown>,
    opts?: { fromAction?: boolean; forceMapContext?: boolean }
  ) => Promise<void>;
  clearSession: (id: string) => void;
  /** On entering the thread: diff shared state vs the session snapshot and,
   *  when something happened elsewhere, append ONE template catch-up turn.
   *  Always refreshes the snapshot. No LLM call, never stacks. */
  syncThreadContext: () => void;
  /** On leaving the thread: refresh the snapshot silently so in-thread
   *  activity is never "caught up" later. */
  snapshotThreadContext: () => void;
  submitCaptureLead: (payload: {
    name?: string; contact?: string; phone?: string; email?: string; note?: string; reason?: string;
  }) => Promise<{ ok: boolean; message: string }>;
  requestHuman: () => Promise<{ ok: boolean; message: string }>;
  pollHumanMessages: () => Promise<void>;
}

interface RequestHumanResponse {
  ok: boolean;
  mode: ConversationMode;
  message: string;
  request_id: string;
  contact_required?: boolean;
  detail?: string;
}

interface ConversationUpdatesResponse {
  messages: Array<LiveConversationMessage & { sender: 'alexander' | 'system' }>;
  mode: ConversationMode;
  human_requested: boolean;
  admin_joined: boolean;
}

/** The analytics session id from useTracking (sessionStorage `ts_session_id`).
 *
 *  vibeStore mints one id per conversation thread; useTracking mints one per
 *  tab and stamps it on every save/view event. Sending both lets the admin
 *  Conversations view join a conversation to the save it produced — without it
 *  that join silently never matches and every conversation reads as abandoned.
 *
 *  Read from the window handle useTracking publishes for non-hook callers,
 *  falling back to sessionStorage. Returns undefined rather than inventing an
 *  id: a wrong join key is worse than a missing one. */
function trackingSessionId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as { __ts_session_id?: string };
  if (w.__ts_session_id) return w.__ts_session_id;
  try {
    return window.sessionStorage.getItem('ts_session_id') || undefined;
  } catch {
    return undefined;   // Safari private mode throws on sessionStorage
  }
}

function generateSessionId(prefix: string = 'vibe'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function generateConversationToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${crypto.randomUUID()}-${crypto.randomUUID()}`;
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

async function syncAssistantMessage(sessionId: string, token: string, content: string, messageId: string): Promise<void> {
  if (!content.trim()) return;
  try {
    await fetch(`${API}/api/vibe-search/sessions/${encodeURIComponent(sessionId)}/assistant-message`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, content, message_id: messageId }),
    });
  } catch { /* best-effort live transcript sync */ }
}

// Stable references so Zustand selectors don't return new arrays each call
// (prevents the "getSnapshot should be cached" React 18 warning + re-renders).
const EMPTY_MESSAGES: VibeChatMessage[] = [];
const EMPTY_APARTMENTS: Apartment[] = [];


/**
 * Read one agentic turn from the SSE endpoint.
 *
 * Returns the final response body — the SAME shape POST /chat returns — so the
 * caller keeps one code path for what a turn MEANS and treats everything
 * before it as progress. Returns null when the server says this session is not
 * on the agent pipeline (or the turn was unsalvageable), and the caller
 * re-issues against /chat.
 *
 * Progress is the entire point: an agentic turn measures 7-13s, and a typing
 * dot for that long reads as a hang. `onNarration` fires per line as tools
 * actually complete.
 */
export async function streamVibeTurn(
  url: string,
  payload: Record<string, unknown>,
  authToken: string | null,
  onNarration: (line: string) => void,
  signal: AbortSignal,
  handlers: {
    // Cards, the moment the search returns (~5-8s into a turn).
    onResults?: (evt: { apartments?: Apartment[]; criteria?: Record<string, unknown> }) => void;
    // A chunk of the closing prose, as the model writes it.
    onTextDelta?: (text: string) => void;
  } = {},
): Promise<any | null> {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify(payload),
    signal,
  });
  // Any transport-level problem falls back rather than surfacing: /chat is
  // still there, and a renter should never see a streaming failure as a
  // search failure.
  if (!res.ok || !res.body) return null;

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let done: any = null;

  try {
    for (;;) {
      const { value, done: finished } = await reader.read();
      if (finished) break;
      buffer += decoder.decode(value, { stream: true });

      // Frames are separated by a blank line. A partial frame stays in the
      // buffer — splitting on newline alone would parse half a JSON object
      // whenever a chunk boundary lands mid-frame.
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        const line = frame.trim();
        if (!line.startsWith('data:')) continue;
        const raw = line.slice(5).trim();
        if (raw === '[DONE]') continue;

        let evt: any;
        try {
          evt = JSON.parse(raw);
        } catch {
          continue;   // never let one malformed frame kill the turn
        }
        if (evt.event === 'narration' && evt.line) onNarration(evt.line);
        else if (evt.event === 'results') handlers.onResults?.(evt);
        else if (evt.event === 'message_delta' && evt.text) handlers.onTextDelta?.(evt.text);
        else if (evt.event === 'done') done = evt;
        else if (evt.event === 'fallback') return null;
      }
    }
  } finally {
    // Releasing the lock lets the connection close when the renter navigates
    // away mid-turn instead of holding it until the server gives up.
    try { reader.releaseLock(); } catch { /* already released */ }
  }
  return done;
}

export const useVibeStore = create<VibeStoreState>()(persist((set, get) => ({
  sessions: {},
  activeSessionId: null,
  isSearching: false,

  // ── Derived getters ──────────────────────────────────────────────
  activeSession: () => {
    const { sessions, activeSessionId } = get();
    return activeSessionId ? sessions[activeSessionId] || null : null;
  },

  activeMessages: () => {
    const session = get().activeSession();
    return session?.messages || EMPTY_MESSAGES;
  },

  activeHasResults: () => {
    const session = get().activeSession();
    return session?.hasResults || false;
  },

  latestResults: () => {
    const messages = get().activeMessages();
    const lastWithApts = [...messages].reverse().find(m => m.apartments?.length);
    return lastWithApts?.apartments || EMPTY_APARTMENTS;
  },

  persistedResults: () => get().activeSession()?.persistedResults || EMPTY_APARTMENTS,
  persistedCriteria: () => get().activeSession()?.persistedCriteria || null,
  latestMapAction: () => get().activeSession()?.mapAction || null,

  // ── Actions ──────────────────────────────────────────────────────
  createSession: (city, source = 'vibe', opts = {}) => {
    const id = generateSessionId(source === 'map' ? 'map-vibe' : 'vibe');

    // Reset all transient Search state (ADR-0001, ADR-0007).
    // Adding a new store to the search flow? Update searchSession.ts — not here.
    // resetState: false is for sessions auto-created on page mount — merely
    // visiting Vibe Search must not wipe the renter's filter panel.
    if (opts.resetState !== false) {
      resetSearchState();
    }

    set(state => ({
      sessions: {
        ...state.sessions,
        [id]: {
          id,
          messages: [],
          hasResults: false,
          city,
          persistedResults: [],
          persistedCriteria: null,
          mapAction: null,
          conversationToken: generateConversationToken(),
          humanMode: 'ai',
          updatedAt: Date.now(),
        },
      },
      activeSessionId: id,
    }));
    return id;
  },

  setActiveSession: (id) => {
    set({ activeSessionId: id });
  },

  sendMessage: async (
    text: string,
    mapContext?: Record<string, unknown>,
    opts?: { fromAction?: boolean; forceMapContext?: boolean }
  ) => {
    const state = get();
    const session = state.activeSession();
    if (!session || !text.trim() || state.isSearching) return;

    const sessionId = session.id;
    const city = session.city;
    const trimmed = text.trim();
    const turnMessageId = `ai-turn-${session.messages.filter(m => m.role === 'user').length}`;

    // Add user message + loading indicator
    set(s => {
      const sess = s.sessions[sessionId];
      if (!sess) return s;
      const cleaned = sess.messages.filter(m => !m.isLoading);
      return {
        sessions: {
          ...s.sessions,
          [sessionId]: {
            ...sess,
            messages: [
              ...cleaned,
              { role: 'user', content: trimmed },
              { role: 'assistant', content: '', isLoading: true },
            ],
            updatedAt: Date.now(),
          },
        },
        isSearching: true,
      };
    });

    const submittedContext = captureThreadContext();

    const history = session.messages
      .filter(m => !m.isLoading)
      .map(m => ({
        role: m.role,
        content: `${m.sender === 'alexander' ? '[Alexander, human locator]: ' : ''}${
          m.criteria ? `${m.content}\n\n[Search criteria used: ${JSON.stringify(m.criteria)}]` : m.content
        }`,
      }));

    try {
      // PR C2 — Dossier (saves, notes, insights, persistent dealbreakers,
      // work location) sent as SOFT context. AI may reference but does not
      // gate results.
      const profileForApi = useUserProfileStore.getState().hasPreferences()
        ? {
            ...useUserProfileStore.getState().getProfileForApi(),
            _soft_context: true,
          }
        : undefined;

      // PR C2 — current_filters is the snake_case mirror of the active
      // filter panel (apartmentStore.filters, the source of truth per
      // ADR-0001). Backend's preferences block sources from here instead
      // of from user_profile criteria fields.
      const currentFilters = toVibeContext(
        useSearchStore.getState().filters,
      );

      const endpoint = '/api/vibe-search/chat';

      // Abort before the 60s K8s ingress timeout so we return a clean error
      // instead of a body-stream-already-read crash on a partial 502 response.
      // Audit #7 — token from in-memory store; cookie covers reload.
      const authToken = typeof window !== 'undefined'
        ? useAuthStore.getState().token
        : null;
      const requestBody = {
        session_id: sessionId,
        tracking_session_id: trackingSessionId(),
        message: trimmed,
        history,
        city,
        ...(mapContext ? { map_context: mapContext } : {}),
        ...(profileForApi ? { user_profile: profileForApi } : {}),
        ...(currentFilters ? { current_filters: currentFilters } : {}),
        ...(opts?.fromAction ? { from_action: true } : {}),
        conversation_token: session.conversationToken,
      };

      // Try the streaming endpoint first. The SERVER decides whether this
      // session is on the agent pipeline — it answers `fallback` if not — so
      // the routing decision stays in one place (should_use_agent) instead of
      // being duplicated here and drifting.
      // Update the in-flight loading bubble in place. Guarded on isLoading: a
      // late frame arriving after the turn resolved must not reopen it.
      const patchLoading = (fn: (m: VibeChatMessage) => VibeChatMessage, markResults = false) => {
        set(s2 => {
          const sess2 = s2.sessions[sessionId];
          if (!sess2) return s2;
          const idx = sess2.messages.findIndex(m => m.isLoading);
          if (idx === -1) return s2;
          const msgs = [...sess2.messages];
          msgs[idx] = fn(msgs[idx]);
          return {
            sessions: {
              ...s2.sessions,
              [sessionId]: { ...sess2, messages: msgs, ...(markResults ? { hasResults: true } : {}) },
            },
          };
        });
      };
      let streamed: any = null;
      try {
        const ctrl = new AbortController();
        const abortAt = setTimeout(() => ctrl.abort(), 55000);
        try {
          streamed = await streamVibeTurn(
            `${API}/api/vibe-search/chat/stream`,
            requestBody,
            authToken,
            (line) => {
              // Appended to the in-flight loading bubble. Guarded on
              // isLoading: a late frame arriving after the turn resolved must
              // not reopen a finished message.
              set(s2 => {
                const sess2 = s2.sessions[sessionId];
                if (!sess2) return s2;
                const msgs = [...sess2.messages];
                const idx = msgs.findIndex(m => m.isLoading);
                if (idx === -1) return s2;
                msgs[idx] = {
                  ...msgs[idx],
                  narration: [...(msgs[idx].narration || []), line],
                };
                return {
                  sessions: { ...s2.sessions, [sessionId]: { ...sess2, messages: msgs } },
                };
              });
            },
            ctrl.signal,
            {
              // Early cards. Setting hasResults also closes the first-search
              // analyzing takeover, so the renter browses while the model is
              // still writing (decided 2026-09-29: dismiss at results, cards
              // first, prose streams below).
              onResults: (evt) => {
                const apts = evt.apartments || [];
                if (!apts.length) return;
                patchLoading(m => ({ ...m, apartments: apts, criteria: evt.criteria || m.criteria }), true);
              },
              onTextDelta: (text) => {
                patchLoading(m => ({ ...m, streamingText: (m.streamingText || '') + text }));
              },
            },
          );
        } finally {
          clearTimeout(abortAt);
        }
      } catch {
        // Streaming is an enhancement, never a new way for search to fail.
        streamed = null;
      }

      const res = streamed ? null : await fetchWithTimeout(`${API}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify(requestBody),
      }, 55000);

      // Read body as text first so we can surface useful info even on non-JSON
      // (502 gateway timeout pages, 5xx HTML errors). Only the outer catch will
      // see 'body stream already read' if something reads res.body first (e.g.
      // a misbehaving service worker); this explicit text-then-parse isolates it.
      const rawBody = res ? await res.text() : '';

      if (res && !res.ok) {
        const statusBlurb = res.status === 502 || res.status === 504
          ? "That query took too long for our AI to finish (>55s). Try a shorter or more specific version: just the neighborhood + budget + bedrooms."
          : `Search failed (HTTP ${res.status}). Please try again in a moment.`;
        throw new Error(statusBlurb);
      }

      // From here down, both paths are identical: `data` is the response body
      // whether it arrived as one POST or as the final frame of a stream.
      let data: any;
      if (streamed) {
        data = streamed;
      } else {
        try {
          data = JSON.parse(rawBody);
        } catch {
          throw new Error("The search response was malformed. Please try again.");
        }
      }
      const apartments: Apartment[] = data.apartments || [];

      // Phase 4 — daily rate limit hit. Show the message as an assistant
      // turn with the isRateLimited flag so the UI can render a CTA.
      if (data.type === 'rate_limited') {
        const rlMessage: VibeChatMessage = {
          role: 'assistant',
          content: data.message || 'Daily search limit reached.',
          isRateLimited: true,
          isAnonymous: !!data.is_anonymous,
        };
        set(s => {
          const sess = s.sessions[sessionId];
          if (!sess) return s;
          return {
            sessions: {
              ...s.sessions,
              [sessionId]: {
                ...sess,
                messages: [...sess.messages.filter(m => !m.isLoading), rlMessage],
              },
            },
            isSearching: false,
          };
        });
        return;
      }

      // Write extracted criteria back to (1) user profile / Dossier
      // for cross-session accumulation, and (2) the active filter panel
      // per ADR-0001 — the filter panel is the source of truth for a
      // Search; Vibe input is an expressive shortcut that writes into it.
      // Without setFiltersFromVibe, extracted filter chips wouldn't appear
      // in the panel and the renter couldn't see/edit what the AI parsed.
      const beforeCriteria = captureThreadContext();
      if (data.criteria) {
        // ADR-0017 transition verb — the Search store owns the vibe merge.
        useSearchStore.getState().applyVibeAction(data.criteria);
      }

      // A turn acknowledges the submitted filters and changes made by its own
      // response. Keep unrelated changes made elsewhere while it was pending
      // available for the return catch-up, including newly saved apartments.
      const afterCriteria = captureThreadContext();
      const acknowledgedContext = { ...submittedContext };
      if (JSON.stringify(beforeCriteria.bedrooms) !== JSON.stringify(afterCriteria.bedrooms)) {
        acknowledgedContext.bedrooms = afterCriteria.bedrooms;
      }
      if (beforeCriteria.maxBudget !== afterCriteria.maxBudget) {
        acknowledgedContext.maxBudget = afterCriteria.maxBudget;
      }
      if (JSON.stringify(beforeCriteria.neighborhoods) !== JSON.stringify(afterCriteria.neighborhoods)) {
        acknowledgedContext.neighborhoods = afterCriteria.neighborhoods;
      }

      const isQuestion = data.type === 'question';
      const wasStreamed = !!get().sessions[sessionId]?.messages.find(m => m.isLoading)?.streamingText;
      const assistantMessage: VibeChatMessage = {
        role: 'assistant',
        content: data.message || '',
        apartments: apartments.length > 0 ? apartments : undefined,
        criteria: data.criteria,
        neighborhoodGroups: data.neighborhood_groups,
        diagnosis: data.diagnosis || null,
        actions: Array.isArray(data.actions) ? data.actions : undefined,
        isQuestion,
        looseMatchCount: Array.isArray(data.loose_matches) ? data.loose_matches.length : undefined,
        isProvisional: !!data.provisional,
        captureOffer: data.capture_offer || null,
        sender: 'ai',
        ...(wasStreamed ? { wasStreamed: true } : {}),
      };

      // §5 (gap-fills v2) — If the last 2 assistant turns produced no new
      // apartment results, append a gentle nudge to start a new search.
      // Prevents endless conversational loops when the user should really
      // be broadening criteria in a fresh session.
      // Never nudge on a question turn: the assistant is waiting on an answer,
      // and telling the renter to start over is the opposite of what the turn
      // is asking for. Prior question turns don't count toward the streak
      // either — being asked something is not a failed search.
      if (apartments.length === 0 && !isQuestion) {
        const sess = get().sessions[sessionId];
        let convTurnsWithoutResults = 0;
        if (sess) {
          for (let i = sess.messages.length - 1; i >= 0; i--) {
            const m = sess.messages[i];
            if (m.isLoading) continue;
            if (m.role !== 'assistant') continue;
            if (m.isQuestion) continue;
            if ((m.apartments?.length ?? 0) > 0) break;
            convTurnsWithoutResults++;
          }
        }
        if (convTurnsWithoutResults >= 2) {
          assistantMessage.content =
            (assistantMessage.content || '') +
            "\n\n*Tip: Hit **+ New Search** at the top to start fresh with different criteria. Sometimes a clean slate finds more than a follow-up can.*";
        }
      }

      // Phase 1 — Context System: persist results across conversational turns.
      // Only replace the persisted set when the backend says this turn was a
      // new/refined search (fit_results or clear). Conversational turns
      // (stay/fly_to) leave the persisted results + criteria intact so the
      // map doesn't go blank when the user asks a follow-up.
      const mapActionRaw = data.map_action || null;
      const mapActionType =
        (mapActionRaw && typeof mapActionRaw === 'object' && 'type' in mapActionRaw)
          ? (mapActionRaw as { type?: string }).type
          : undefined;
      const shouldUpdatePersisted =
        apartments.length > 0 &&
        (mapActionType === undefined || mapActionType === 'fit' || mapActionType === 'fit_results' || mapActionType === 'clear');

      set(s => {
        const sess = s.sessions[sessionId];
        if (!sess) return s;
        return {
          sessions: {
            ...s.sessions,
            [sessionId]: {
              ...sess,
              messages: [...sess.messages.filter(m => !m.isLoading), assistantMessage],
              searchFilters: { ...useSearchStore.getState().filters },
              contextSnapshot: {
                ...acknowledgedContext,
                savedNames: sess.contextSnapshot?.savedNames ?? submittedContext.savedNames,
              },
              hasResults: sess.hasResults || apartments.length > 0,
              persistedResults: shouldUpdatePersisted ? apartments : sess.persistedResults,
              persistedCriteria: shouldUpdatePersisted
                ? (data.criteria || sess.persistedCriteria)
                : sess.persistedCriteria,
              mapAction: mapActionRaw,
            },
          },
          isSearching: false,
        };
      });
      void syncAssistantMessage(sessionId, session.conversationToken, assistantMessage.content, turnMessageId);

      // Background summary fetch — forward `actions` so the prompt can ground
      // prose in pivot context (median/p25 + pivot directions) when the
      // renter is in a thin-results state and pivots are about to render.
      if (data.summary_pending && apartments.length > 0) {
        try {
          const summaryRes = await fetchWithTimeout(`${API}/api/vibe-search/summary`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              criteria: data.criteria,
              apartments: apartments.slice(0, 8),
              user_message: trimmed,
              city,
              // Attributes this deferred Haiku call to the conversation that
              // triggered it — without it the summary's cost floats free of
              // any session and per-conversation spend undercounts.
              session_id: sessionId,
              tracking_session_id: trackingSessionId(),
              user_profile: profileForApi,
              actions: data.actions || [],
              // Requirements the extractor could not express as criteria (a
              // yard, a 6-month lease). The summary acknowledges these instead
              // of writing prose that implies everything was honored. The
              // server prefers its own copy; this is a fallback for sessions
              // it can't look up.
              unparsed_requirements: data.unparsed_requirements || [],
            }),
          });
          const summaryData = await summaryRes.json();
          if (summaryData.summary) {
            set(s => {
              const sess = s.sessions[sessionId];
              if (!sess) return s;
              const msgs = [...sess.messages];
              const lastIdx = msgs.findLastIndex(m => m.role === 'assistant' && m.apartments?.length);
              if (lastIdx >= 0) {
                // D3: lead the result list with the buildings the summary
                // actually names, so the cards match what the prose describes.
                // Each group keeps its existing ranked order.
                const summaryLc = String(summaryData.summary).toLowerCase();
                const apts = msgs[lastIdx].apartments || [];
                const named: typeof apts = [];
                const rest: typeof apts = [];
                for (const a of apts) {
                  if (a?.name && summaryLc.includes(a.name.toLowerCase())) named.push(a);
                  else rest.push(a);
                }
                const reordered = named.length ? [...named, ...rest] : apts;
                msgs[lastIdx] = { ...msgs[lastIdx], content: summaryData.summary, apartments: reordered };
              }
              return {
                sessions: { ...s.sessions, [sessionId]: { ...sess, messages: msgs } },
              };
            });
            void syncAssistantMessage(sessionId, session.conversationToken, summaryData.summary, turnMessageId);
          }
        } catch (e) {
          console.error('Summary fetch failed:', e);
        }
      }
    } catch (e) {
      console.error('Vibe search error:', e);
      const rawMsg = e instanceof Error ? e.message : '';
      // AbortError is our own 55s safety abort — show a specific retry hint.
      const isAbort = e instanceof Error && (e.name === 'AbortError' || rawMsg.includes('aborted'));
      // Raw connectivity failures ("Failed to fetch" etc.) carry no useful
      // detail — collapse them into the board's CONNECTION LOST copy.
      const isNetwork =
        e instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(rawMsg);
      const friendly = isAbort
        ? "That query took too long for our AI to finish (>55s). Try a shorter or more specific version: just the neighborhood + budget + bedrooms."
        : (!isNetwork && rawMsg && rawMsg.length < 240
            ? rawMsg
            : "Your vibe's saved. Tap retry to pick up where you left off.");
      set(s => {
        const sess = s.sessions[sessionId];
        if (!sess) return s;
        return {
          sessions: {
            ...s.sessions,
            [sessionId]: {
              ...sess,
              messages: [
                ...sess.messages.filter(m => !m.isLoading),
                { role: 'assistant', content: friendly, isError: true },
              ],
            },
          },
          isSearching: false,
        };
      });
    }
  },

  clearSession: (id) => {
    set(s => {
      const newSessions = { ...s.sessions };
      delete newSessions[id];
      return {
        sessions: newSessions,
        activeSessionId: s.activeSessionId === id ? null : s.activeSessionId,
      };
    });
  },

  syncThreadContext: () => {
    const state = get();
    const session = state.activeSession();
    if (!session) return;
    const curr = captureThreadContext();
    const prev = session.contextSnapshot;
    // First visit for this session: just take the snapshot.
    if (prev) {
      const real = session.messages.filter(m => !m.isLoading);
      const last = real[real.length - 1];
      const parts = diffThreadContext(prev, curr);
      // Only a real conversation gets a catch-up, and never two in a row.
      if (parts.length > 0 && real.length >= 2 && !last?.isCatchUp) {
        const catchUp: VibeChatMessage = {
          role: 'assistant',
          content:
            `Quick catch-up: while you were exploring you ${parts.join(', ')}. ` +
            `Want me to run with that, or keep going from where we left off?`,
          isCatchUp: true,
          actions: [{
            id: 'catchup-search',
            label: 'Search with my updates',
            kind: 'send_message',
            message: 'Search again using my current filters',
          }],
        };
        set(s => {
          const sess = s.sessions[session.id];
          if (!sess) return s;
          return {
            sessions: {
              ...s.sessions,
              [session.id]: { ...sess, messages: [...sess.messages.filter(m => !m.isLoading), catchUp] },
            },
          };
        });
      }
    }
    set(s => {
      const sess = s.sessions[session.id];
      if (!sess) return s;
      return { sessions: { ...s.sessions, [session.id]: { ...sess, contextSnapshot: curr, searchFilters: { ...useSearchStore.getState().filters } } } };
    });
  },

  requestHuman: async () => {
    const session = get().activeSession();
    if (!session) return { ok: false, message: 'Start a conversation first.' };
    try {
      const res = await fetch(`${API}/api/vibe-search/sessions/${encodeURIComponent(session.id)}/request-human`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.conversationToken }),
      });
      const data = await res.json() as RequestHumanResponse;
      if (!res.ok) throw new Error(data.detail ?? 'Could not notify Alexander.');
      set(s => ({ sessions: { ...s.sessions, [session.id]: {
        ...s.sessions[session.id],
        humanRequested: true,
        humanMode: data.mode,
        messages: [...s.sessions[session.id].messages, {
          role: 'assistant', sender: 'system', content: data.message,
          messageId: `system-human-request-${data.request_id}`,
          captureOffer: data.contact_required ? {
            reason: 'human',
            headline: 'Where should Alexander reach you if you leave this page?',
            fields: ['contact'], apartments: [], cta: 'Send my contact',
          } : null,
        }],
      } } }));
      await get().pollHumanMessages();
      return { ok: true, message: data.message };
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Could not notify Alexander.';
      set(s => ({ sessions: { ...s.sessions, [session.id]: {
        ...s.sessions[session.id], messages: [...s.sessions[session.id].messages, {
          role: 'assistant', sender: 'system', content: message,
          messageId: `system-human-request-error-${Date.now()}`,
        }],
      } } }));
      return { ok: false, message };
    }
  },

  pollHumanMessages: async () => {
    const session = get().activeSession();
    if (!session?.conversationToken) return;
    if (typeof document !== 'undefined' && (document.visibilityState !== 'visible' || !document.hasFocus())) return;
    const seenMessageIds = session.messages
      .filter(message => message.sender === 'alexander' && message.messageId)
      .map(message => message.messageId as string);
    try {
      const res = await fetch(`${API}/api/vibe-search/sessions/${encodeURIComponent(session.id)}/updates`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.conversationToken, seen_message_ids: seenMessageIds }),
      });
      if (!res.ok) return;
      const data = await res.json() as ConversationUpdatesResponse;
      const incoming = Array.isArray(data.messages) ? data.messages : [];
      set(s => {
        const current = s.sessions[session.id];
        if (!current) return s;
        const known = new Set(current.messages.map(m => m.messageId).filter(Boolean));
        const additions: VibeChatMessage[] = incoming
          .filter(m => m.message_id && !known.has(m.message_id))
          .map(m => ({ role: 'assistant', sender: m.sender, content: m.content, messageId: m.message_id }));
        return { sessions: { ...s.sessions, [session.id]: {
          ...current, messages: [...current.messages, ...additions],
          humanMode: data.mode ?? 'ai', humanRequested: !!data.human_requested,
          updatedAt: additions.length ? Date.now() : current.updatedAt,
        } } };
      });
    } catch { /* polling is best effort */ }
  },

  submitCaptureLead: async (payload: {
    name?: string; contact?: string; phone?: string; email?: string; note?: string; reason?: string;
  }): Promise<{ ok: boolean; message: string }> => {
    const sessionId = get().activeSessionId;
    const conversationToken = get().activeSession()?.conversationToken;
    if (!sessionId || !conversationToken) return { ok: false, message: 'No active conversation.' };
    try {
      const res = await fetchWithTimeout(`${API}/api/vibe-search/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, conversation_token: conversationToken, ...payload }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { ok: false, message: data?.detail || 'Could not send your details. Please try again.' };
      }
      // Mark the offering turn as done so the card renders a confirmation.
      set(s => {
        const sess = s.sessions[sessionId];
        if (!sess) return s;
        const messages = sess.messages.map(m =>
          m.captureOffer && !m.captureDone ? { ...m, captureDone: data.message || 'Sent.' } : m,
        );
        return { sessions: { ...s.sessions, [sessionId]: { ...sess, messages } } };
      });
      return { ok: true, message: data.message || 'Sent.' };
    } catch {
      return { ok: false, message: 'Could not send your details. Please try again.' };
    }
  },

  snapshotThreadContext: () => {
    const session = get().activeSession();
    if (!session) return;
    const curr = captureThreadContext();
    set(s => {
      const sess = s.sessions[session.id];
      if (!sess) return s;
      return { sessions: { ...s.sessions, [session.id]: { ...sess, contextSnapshot: curr, searchFilters: { ...useSearchStore.getState().filters } } } };
    });
  },
}), {
  name: 'tacostreet_vibe_sessions',
  // safeLocal (not raw localStorage): private/incognito mode throws
  // SecurityError on storage access; safeLocal degrades to a no-op.
  storage: createJSONStorage(() => ({
    getItem: (k: string) => safeLocal.get(k),
    setItem: (k: string, v: string) => safeLocal.set(k, v),
    removeItem: (k: string) => safeLocal.remove(k),
  })),
  // Trim + cap on every write so a long conversation can't brush the
  // localStorage quota. isSearching and derived getters never persist.
  partialize: (s) => {
    const pruned = pruneSessions(s.sessions, s.activeSessionId);
    const sessions: Record<string, VibeSession> = {};
    for (const [id, sess] of Object.entries(pruned.sessions)) {
      sessions[id] = trimSessionForStorage(sess);
    }
    return { sessions, activeSessionId: pruned.activeSessionId } as Partial<VibeStoreState>;
  },
  // Prune expired sessions on load (TTL check against rehydration time).
  merge: (persisted, current) => {
    const p = (persisted ?? {}) as Partial<VibeStoreState>;
    const pruned = pruneSessions(
      (p.sessions as Record<string, VibeSession>) || {},
      (p.activeSessionId as string | null) ?? null,
    );
    for (const session of Object.values(pruned.sessions)) {
      if (!session.conversationToken) session.conversationToken = generateConversationToken();
    }
    // Keep the thread in history, but stop auto-resuming it once it has gone
    // cold — see vibeSessionResume for why that distinction matters.
    const activeSessionId = resolveResumableSessionId(
      pruned.sessions, pruned.activeSessionId,
    );
    // Older agent turns used `fit`, which this store previously ignored.
    // Recover their real result snapshot from the conversation, not previews.
    for (const sess of Object.values(pruned.sessions)) {
      if (!sess.persistedResults?.length && sess.mapAction?.type !== 'clear') {
        const lastSearch = [...sess.messages].reverse().find(m =>
          m.role === 'assistant' && !m.isQuestion && !m.isProvisional && m.apartments?.length);
        if (lastSearch) {
          sess.persistedResults = lastSearch.apartments!;
          sess.persistedCriteria = lastSearch.criteria ?? sess.persistedCriteria;
        }
      }
    }
    const active = activeSessionId ? pruned.sessions[activeSessionId] : undefined;
    if (active) {
      // Legacy conversations lack a full criteria snapshot. Start from their
      // latest extraction, then preserve explicit clears in the saved baseline.
      const lastCriteria = [...active.messages].reverse().find(m => m.role === 'assistant' && m.criteria)?.criteria;
      const baseline = active.contextSnapshot;
      const criteria = {
        ...(lastCriteria ?? active.persistedCriteria ?? {}),
        ...(baseline ? { bedrooms: baseline.bedrooms, neighborhoods: baseline.neighborhoods, budget_max: baseline.maxBudget } : {}),
      };
      restoreConversationInNewTab(active.city, active.searchFilters, criteria);
    }
    return { ...current, sessions: pruned.sessions, activeSessionId };
  },
  version: 1,
}));
